import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import type { EventMember } from '../../event-manager-client';
import { getInitials } from './utils';

const BADGE_COLORS: Record<string, string> = {
  matcha: 'bg-matcha-100 text-matcha-700 dark:bg-matcha-900/30 dark:text-matcha-400',
  tangerine: 'bg-tangerine-100 text-tangerine-700 dark:bg-tangerine-900/30 dark:text-tangerine-400',
  blueberry: 'bg-blueberry-100 text-blueberry-700 dark:bg-blueberry-900/30 dark:text-blueberry-400',
};

interface MemberCardProps {
  member: EventMember;
  onClick?: () => void;
  isClayHost?: boolean;
  accountBadge?: { label: string; variant: string } | null;
  isPending?: boolean;
  onSwapDrop?: (draggedMemberId: number) => void;
}

export function MemberCard({ member, onClick, isClayHost, accountBadge, isPending, onSwapDrop }: MemberCardProps) {
  const [dragging, setDragging] = useState(false);
  const [dropOver, setDropOver] = useState(false);

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData('text/plain', String(member.id));
    e.dataTransfer.effectAllowed = 'move';
    setDragging(true);
  };

  const handleDragEnd = () => {
    setDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (!onSwapDrop) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent) => {
    if (!onSwapDrop) return;
    e.preventDefault();
    e.stopPropagation();
    setDropOver(false);
    const draggedId = Number(e.dataTransfer.getData('text/plain'));
    if (!isNaN(draggedId) && draggedId !== member.id) {
      onSwapDrop(draggedId);
    }
  };

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragEnter={(e) => { if (onSwapDrop) { e.stopPropagation(); setDropOver(true); } }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDropOver(false); }}
      onDrop={handleDrop}
      onClick={onClick}
      className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border bg-white dark:bg-nightshade-900 cursor-grab active:cursor-grabbing hover:border-blueberry-300 dark:hover:border-blueberry-600 transition-colors ${
        dropOver ? 'border-blueberry-400 dark:border-blueberry-500 shadow-md' : ''
      } ${
        isClayHost
          ? 'border-l-[3px] border-l-blueberry-500 border-nightshade-200 dark:border-nightshade-700'
          : isPending
            ? 'border-l-[3px] border-l-tangerine-400 border-nightshade-200 dark:border-nightshade-700'
            : 'border-nightshade-200 dark:border-nightshade-700'
      } ${dragging ? 'opacity-50' : ''}`}
    >
      {/* Initials circle */}
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
        isClayHost
          ? 'bg-blueberry-200 dark:bg-blueberry-800/40'
          : 'bg-blueberry-100 dark:bg-blueberry-900/30'
      }`}>
        <span className={`text-xs font-semibold ${
          isClayHost
            ? 'text-blueberry-700 dark:text-blueberry-300'
            : 'text-blueberry-600 dark:text-blueberry-400'
        }`}>
          {getInitials(member.contact_name)}
        </span>
      </div>

      {/* Name + badges + company + title */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1">
          <p className="text-xs font-medium text-nightshade-800 dark:text-nightshade-200 truncate">
            {member.contact_name ?? 'Unknown'}
          </p>
          {isClayHost && (
            <span className="text-[8px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-blueberry-100 text-blueberry-700 dark:bg-blueberry-900/30 dark:text-blueberry-400 flex-shrink-0">
              Clay Host
            </span>
          )}
          {accountBadge && (
            <span className={`text-[8px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded flex-shrink-0 ${BADGE_COLORS[accountBadge.variant] ?? ''}`}>
              {accountBadge.label}
            </span>
          )}
          {!member.contact_linkedin && (
            <span title="LinkedIn missing" className="flex-shrink-0">
              <AlertTriangle className="w-3 h-3 text-blueberry-500 flex-shrink-0" />
            </span>
          )}
        </div>
        {member.account_name && (
          <p className="text-[10px] text-nightshade-500 dark:text-nightshade-400 truncate">
            {member.account_name}
          </p>
        )}
        {member.contact_title && (
          <p className="text-[10px] text-nightshade-400 dark:text-nightshade-500 truncate">
            {member.contact_title}
          </p>
        )}
      </div>
    </div>
  );
}
